<template>
  <el-select
    class="width-max"
    v-model="currentValue"
    filterable
    remote
    :multiple="multiple"
    :collapse-tags="collapseTags"
    :multiple-limit="multipleLimit"
    :disabled="disabled"
    :clearable="clearable"
    :placeholder="placeholder"
    @focus="focus"
    @visible-change="visibleChange"
    @change="change"
    :remote-method="remoteMethod"
    :loading="loading"
    :autocomplete="currentAutocomplete">
    <el-option
      v-for="item in items"
      :key="item[valueKey]"
      :label="getLabel(item)"
      :value="item[valueKey]">
      <slot v-bind:item="item">
        <span>{{item[labelKey]}}</span>
        <span class="info">/{{item[idKey]}}</span>
      </slot>
    </el-option>
  </el-select>
</template>

<script>

export default {
  name: 'RemoteSelect',
  props: {
    value: [String, Number, Array, Object],
    placeholder: {
      type: String,
      default: '请输入关键字',
    },
    /**
     * 浏览器自动填充
     * 强制浏览器不自动填充的方法是为autocomplete设置一个随机的字符串，例如：autocomplete="nope" https://developer.mozilla.org/zh-CN/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
     * 语意化 e.g. autocomplete="new-user-street-address". https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164
     */
    autocomplete: String,
    multiple: Boolean,
    /** 多选时是否折叠标签为一行（+N） */
    collapseTags: {
      type: Boolean,
      default: false,
    },
    multipleLimit: Number,
    disabled: Boolean,
    clearable: Boolean,
    label: Function,
    valueKey: {
      type: String,
      default: 'id',
    },
    keywordKey: {
      type: String,
      default: 'keyword',
    },
    labelKey: {
      type: String,
      default: 'name',
    },
    idKey: {
      type: String,
      default: 'id',
    },
    queryRequest: Function,
    queryParams: {
      type: Object,
      default: () => {
        return {}
      },
    },
    findRequest: Function,
    findParams: {
      type: Object,
      default: () => {
        return {}
      },
    },
    debounce: {
      type: Number,
      default: 300,
    },
  },
  computed: {
    currentAutocomplete () {
      return this.autocomplete || `select-search-${this._uid}`
    }
  },
  watch: {
    value () {
      !_.isEqual(this.value, this.currentValue) && this.setCurrentValue()
    },
  },
  data () {
    return {
      items: [],
      loading: false,
      fetchSeq: 0,
      currentValue: this.multiple ? [] : null,
    }
  },
  created () {
    this.value && this.setCurrentValue()
    // 输入搜索走防抖；点击展开时会 cancel 后立即请求
    this.debouncedSetOptions = _.debounce((params) => {
      this.fetchOptions(params)
    }, this.debounce)
  },
  mounted () {},
  beforeDestroy () {
    if (this.debouncedSetOptions && this.debouncedSetOptions.cancel) {
      this.debouncedSetOptions.cancel()
    }
  },
  methods: {
    getLabel (item) {
      if (this.label) {
        return this.label(item)
      } else {
        return item[this.labelKey] + '/' + item[this.idKey]
      }
    },
    isEmptyValue () {
      if (this.multiple) {
        return this.currentValue.length === 0
      } else {
        return !this.currentValue
      }
    },
    /**
     * 仅保留当前选中项，避免清空 options 后输入框丢失 label
     */
    getSelectedItems () {
      if (this.multiple) {
        const values = this.currentValue || []
        return (this.items || []).filter(item => values.indexOf(item[this.valueKey]) !== -1)
      }
      if (!this.currentValue) {
        return []
      }
      const selected = this.findOption({[this.valueKey]: this.currentValue})
      return selected ? [selected] : []
    },
    /**
     * 进入加载态前先清掉旧的下拉结果（保留选中项）
     */
    prepareLoadingOptions () {
      this.items = this.getSelectedItems()
      this.loading = true
    },
    emptyOptions () {
      this.items = []
    },
    findOption (params) {
      return _.find(this.items, params)
    },
    setCurrentValue () {
      if (this.multiple) {
        this.currentValue = this.value || []
        this.currentValue.forEach((value) => {
          this.setOption(value)
        })
      } else {
        this.currentValue = this.value
        this.setOption(this.currentValue)
      }
    },
    setOption (value) {
      // 列表中不存在该列
      let params = {[this.valueKey]: value}
      if (this.findRequest && value && !this.findOption(params)) {
        this.findRequest({...params, ...this.findParams}).then(({data}) => {
          this.items.push(data)
          this.$emit('find-success', data)
        })
      }
    },
    fetchOptions (params = {}) {
      const requestId = ++this.fetchSeq
      this.loading = true
      // 实际发起请求时清掉旧下拉（保留选中项），避免仍短暂展示上一次结果
      const selectedItems = this.getSelectedItems()
      this.items = selectedItems
      this.queryRequest({...params, ...this.queryParams}).then(({data}) => {
        if (requestId !== this.fetchSeq) {
          return
        }
        const items = Array.isArray(data.data) ? data.data : []
        const merged = items.slice()
        selectedItems.forEach((selected) => {
          const key = selected[this.valueKey]
          const exists = merged.some((item) => String(item[this.valueKey]) === String(key))
          if (!exists) {
            merged.unshift(selected)
          }
        })
        this.items = merged
        if (!items.length) {
          this.$message.warning('暂无数据，请调整您的搜索条件再试试！')
        }
        this.$emit('query-success', data)
      }).finally(() => {
        if (requestId === this.fetchSeq) {
          this.loading = false
        }
      })
    },
    remoteMethod (keyword) {
      if (keyword !== '') {
        this.debouncedSetOptions({[this.keywordKey]: keyword})
      } else if (!this.loading) {
        // 清空关键字或点开时：拉默认列表，而不是只剩当前选中项
        this.fetchOptions()
      }
    },
    change (value) {
      this.$emit('input', value)
      this.$emit('change', value ? this.findOption({[this.valueKey]: value, ...this.findParams}) : value)
    },
    /**
     * 收起时只留选中项，避免下次展开先画出上一轮完整下拉
     */
    visibleChange (visible) {
      if (visible) {
        return
      }
      if (this.debouncedSetOptions && this.debouncedSetOptions.cancel) {
        this.debouncedSetOptions.cancel()
      }
      this.fetchSeq++
      this.loading = false
      this.items = this.getSelectedItems()
    },
    focus () {
      // 已有选中项时也要重新拉列表，否则下拉只剩当前选中的一项
      this.prepareLoadingOptions()
      if (this.debouncedSetOptions && this.debouncedSetOptions.cancel) {
        this.debouncedSetOptions.cancel()
      }
      this.fetchOptions()
    },
  },
}
</script>
<style lang="scss">
// 全局CSS
</style>
<style lang="scss"
       scoped>
// 带作用域的CSS
// https://vue-loader.vuejs.org/zh-cn/features/scoped-css.html
</style>
<style lang="scss"
       module>
// 带作用域的CSS
// 绑定class方式 <p :class="$style.red"></p>
// https://vue-loader.vuejs.org/zh-cn/features/css-modules.html
</style>



// WEBPACK FOOTER //
// core/components/RemoteSelect.vue